<template>
  <o-loading
    :full-page="false"
    :active="isLoading"
    :can-cancel="false"
  ></o-loading>
  <div class="list-tab is-border-radius-sm order-details p-2 is-clipped">
    <div class="column card-tab-details">
      <div class="columns">
        <div class="column is-4">
          <div class="tabs-veritcal">
            <div
              v-for="(tab, index) in tabs"
              :key="index"
              class="tab-item is-flex is-align-items-center"
              :class="{ active: activeTabIndex === index }"
              @click="setData(index)"
            >
              <div
                class="tab-icon is-pulled-left is-flex is-align-items-center is-justify-content-center"
              >
                <i :class="tab.icon" class="has-text-white"></i>
              </div>
              <h3
                class="has-text-black-bis is-size-3 has-text-weight-bold"
              >
                <span class="line-height is-inline-block">{{ tab.title }}</span><br/>
                <span class="has-text-weight-normal line-height is-inline-block">{{ tab.description }}</span>
              </h3>
            </div>
          </div>
        </div>
        <div class="column">
          <div class="tab-content has-background-grey-lighter is-border-radius-sm">
            <div class="column">
              <div class="columns is-multiline">
                <div class="column">
                  <div class="columns">
                    <div class="column is-narrow">
                      <button
                        class="button has-background-black-bis has-text-white is-size-4"
                        @click="importLAZ"
                        v-if="showImportLvlZone && !isDisabledOnPermission">
                        <span>Get From Project</span>
                      </button>
                    </div>
                    <div class="column">
                      <table-toolbar
                        searchPlaceHolder="Search"
                        :toolbarOpts="tableColOpts.toolBar"
                        @handleAction="handleAction"
                        :key="activeTabIndex"
                      />
                    </div>
                  </div>
                  <data-table
                    :key="rerenderKey"
                    :apiMode="true"
                    :tableOpts="tableColOpts"
                    :loadData="loadData"
                    @cell-clicked="handleAction"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <o-modal
    v-if="showAddEditModal"
    :active="showAddEditModal"
    :canCancel="false"
    class="modal-xs"
  >
    <div class="modal-card">
      <header class="modal-card-head is-borderless">
        <h3 class="card-header-title p-0 has-text-black-bis">
          {{ editMode ? `Update ${getModalTitle}` : `Add ${getModalTitle}` }}
        </h3>
      </header>
      <section class="modal-card-body is-visible has-text-black-bis">
        <label class="line-height">
          {{ editMode ? `Update ${getModalInputLabel}:` : `New ${getModalInputLabel}:` }}
        </label>
        <input class="input generic-input" v-model.trim="newName" required />
      </section>
      <footer class="modal-card-foot is-justify-content-flex-end">
        <button
          class="button is-outlined"
          @click="
            showAddEditModal = false;
            newName = '';
            editMode = false;
          "
        >
          Cancel
        </button>
        <button
          class="button has-background-black-bis"
          :disabled="$_.isEmpty(newName)"
          @click="handleAddUpdate(true)"
        >
          {{ editMode ? "Update" : "Add" }}
        </button>
      </footer>
    </div>
    <o-loading :active="isLoading" :full-page="false" />
  </o-modal>
  <activity-log
      :id="activityDetail.id"
      :projectId="activityDetail.projectId"
      :isActive="isActivityLogOpen"
      :orderName="activityDetail.orderName"
      @update:isActive="isActivityLogOpen = false"
      :type = "module"
    ></activity-log>
</template>
<script>
import { computed, onBeforeMount, reactive, toRefs } from "vue";
import { v4 as uuid } from 'uuid';
import { useStore } from "vuex";
import { useToast } from "vue-toastification";
import { map, isEmpty, inRange, uniq, get, filter, difference, remove } from "lodash";
import Tags from "@/models/Companies";
import DataTable from "@/components/table-fields/DataTable.vue";
import TableToolbar from "@/components/table-fields/TableToolbar.vue";
import { DialogProgrammatic } from "@/components/Dialog";
import Projects from "@/models/Projects";
import ActivityLog from '@/components/ActivityLog.vue';
import PermissionMixin from '@/components/mixins/PermissionMixin';

export default {
  name: "OrderRouting",
  props: {
    rowData: Object,
  },
  components: {
    DataTable,
    TableToolbar,
    ActivityLog
  },
  setup(props, { emit }) {
    const store = useStore();
    const toast = useToast();

    const tabs = [
      {
        title: "Tags",
        icon: "icon-attachedtagsOL",
        id: 'tag',
        description: 'Define and edit your custom project tags'
      },
      {
        title: 'Levels',
        icon: "icon-stack",
        id: 'level',
        description: 'Define and edit your custom project levels'
      },
      {
        title: 'Zones',
        icon: "icon-attachedlocationsOL",
        id: 'zone',
        description: 'Define and edit your custom project zones'
      }
    ];

    const tableColOpts = {
      defaultSortField: "name",
      defaultSortOrder: "asc",
      rearrangeCols: false,
      isCheckable: false,
      toolBar: {
        buttons: [
          {
                label: 'History',
                event: 'activity',
                icon: 'icon-activityhistory',
          },
          {
            event: "addNew",
            icon: "icon-addnew",
            isLine: true,
            tooltip: "Add New",
            permissionModule: 'app&projects',
            alterDataKey: 'alterProject',
          },
        ],
        bulkActions: () => {},
      },
      fields: [
        {
          title: "Name",
          name: "name",
          width: "400px",
          id: "name",
          sortField: "name",
          prop: "name",
          defaultVisible: true,
        },
        {
          title: "Actions",
          name: "table-field-actions",
          id: "action",
          hideFromChooser: true,
          alwaysVisible: true,
          defaultVisible: true,
          width: "112px",
          permissionModule: "app&projects",
          permissionKey: "projects",
          inputProps: {
            showEdit: true,
            groupButtons() {
              return [
                {
                  icon: "icon-edit",
                  event: "edit",
                  tooltip: "Edit",
                  permissionModule: 'app&projects',
                  alterDataKey: 'alterProject',
                },
                {
                  icon: "icon-bin",
                  event: "delete",
                  tooltip: "delete",
                  disabled(row) {
                    return row.canDelete;
                  },
                  permissionModule: 'app&projects',
                  alterDataKey: 'alterProject',
                },
              ];
            },
          },
        },
      ],
    };

  const { getPermissionValues } = PermissionMixin();

    const data = reactive({
      project: {},
      newName: "",
      tableData: [],
      selectedRow: {},
      rerenderKey: 0,
      editMode: false,
      existingNames: [],
      isLoading: false,
      activeTabIndex: 0,
      dataLoaded: false,
      showAddEditModal: false,
      searchVal: '',
      activityDetail: {
        id: '',
        projectId: '',
        orderName: '',
      },
      isActivityLogOpen: false,
    });

    const selectedTab = computed(() => tabs[data.activeTabIndex])

    const setData = (idx) => {
      data.activeTabIndex = idx;
      data.searchVal = ''
      refreshTable()
    }

    const loadLvl = async() => {
      const lvlArr = []
      let deletableLevels = []
      // check for deletable levels
      try {
        const usedLvl = (await Projects.getLAZFromCards({
          projectId: props.rowData._id,
          fieldType:  'levels',
        })).data;
        deletableLevels = difference(data.project.levels, usedLvl);
        deletableLevels = deletableLevels.filter((e) => e);
      } catch (e) {
        console.error('Error: ', e)
      }
      if (!isEmpty(get(data.project, 'levels', ''))) {
        data.project.levels.forEach((lvl) => {
          lvlArr.push({
            uuid: uuid(),
            name: lvl,
            canDelete: !deletableLevels.includes(lvl)
          })
        })
      }
      data.tableData = lvlArr.filter((l) => l.name.toLowerCase().includes(data.searchVal.toLowerCase()))
      return {data: data.tableData, total: lvlArr.length}
    }

    const loadZone = async() => {
      const zoneArr = []
      let deletableZones = []
      // check for deletable levels
      try {
        const usedZone = (await Projects.getLAZFromCards({
          projectId: props.rowData._id,
          fieldType:  'zones',
        })).data;
        deletableZones = difference(data.project.zones, usedZone);
        deletableZones = deletableZones.filter((e) => e);
      } catch (e) {
        console.error('Error: ', e)
      }
      if (!isEmpty(get(data.project, 'zones', ''))) {
        data.project.zones.forEach((zone) => {
          zoneArr.push({
            uuid: uuid(),
            name: zone,
            canDelete: !deletableZones.includes(zone)
          })
        })
      }
      data.tableData = zoneArr.filter((z) => z.name.toLowerCase().includes(data.searchVal.toLowerCase()))
      return {data: data.tableData, total: zoneArr.length}
    }


    const loadData = async (commonParam) => {
      try {
        if (selectedTab.value.id === 'level') {
          return loadLvl()
        } else if (selectedTab.value.id === 'zone') {
          return loadZone()
        } else {
          const res = await Tags.getTags({
            ...commonParam,
            project: props.rowData._id,
            search: data.searchVal
          });
          data.existingNames.push(
            ...map(res.data, (tag) => tag.name.toLowerCase())
          );
          data.existingNames = uniq(data.existingNames);
          data.tableData = res.data;
          return res;
        }
      } catch (e) {
        console.error("Error: ", e);
      }
    };

    const setModalActive = (key, val) => {
      data[key] = val;
    };

    const setEdit = (row) => {
      data.selectedRow = row;
      setModalActive("showAddEditModal", true);
      data.editMode = true;
      data.newName = row.name;
    };

    const deleteTag = (tag) => {
      DialogProgrammatic.confirm({
        title: "Delete Tag?",
        message:
          "Are you sure you want to delete this Tag? This operation will remove the tags from all the orders if used.",
        okButton: "Yes",
        cancelButton: "Cancel",
        onConfirm: async () => {
          try {
            data.isLoading = true;
            await Tags.archiveTag(tag._id, props.rowData._id);
            data.existingNames = data.existingNames.filter(
              (name) => name !== tag.name.toLowerCase()
            );
            refreshTable();
            toast.success(`Successfully archived the tag ${tag.name}`);
          } catch (error) {
            console.error("Error", error);
          } finally {
            data.isLoading = false;
          }
        },
      });
    };

    const deleteLvlZone = async (row) => {
      const type = selectedTab.value.id === 'level' ? 'level' : 'zone';
      const fieldType = type === 'level' ? 'levels' : 'zones';
      data.isLoading = true;
      let usedList;
      try {
        usedList = (await Projects.getLAZFromCards({ projectId: props.rowData._id, fieldType })).data;
      } catch (e) {
        console.error('Error checking usage: ', e);
        toast.error(`Could not verify if "${row.name}" is in use. Please try again.`);
        data.isLoading = false;
        return;
      }
      data.isLoading = false;
      if (usedList.includes(row.name)) {
        toast.error(`This ${type} "${row.name}" is currently used in orders and cannot be deleted.`);
        return;
      }
      DialogProgrammatic.confirm({
        title: `Delete ${type}?`,
        message: `Are you sure you want to delete this ${type}?`,
        okButton: "Yes",
        cancelButton: "Cancel",
        onConfirm: async () => {
          try {
            data.isLoading = true;
            remove(data.project[fieldType], (v) => v === row.name);
            data.project = await Projects.update(data.project);
            refreshTable();
            toast.success(`Successfully archived the  ${row.name}`);
          } catch (error) {
            console.error("Error", error);
          } finally {
            data.isLoading = false;
          }
        },
      });
    };

    const searchData = (val) => {
      if ((!isEmpty(data.searchVal) && isEmpty(val)) || (!isEmpty(val) && data.searchVal !== val)) {
        data.searchVal = val
        refreshTable()
      }
    }

    const actionMapper = {
      activity: () => {
          data.activityDetail = {
            id: props.rowData._id,
            projectId: props.rowData._id,
            orderName: props.rowData.name,
          };
          data.isActivityLogOpen = true;
      },
      addNew: () => setModalActive("showAddEditModal", true),
      edit: (row) => setEdit(row),
      delete: (row) =>  selectedTab.value.id === 'tag' ? deleteTag(row) : deleteLvlZone(row),
      search: (val) => searchData(val)
    };

    const handleAction = async (action) => {
      try {
        if (action === 'addNew') {
          action = {type: ''}
          action.type = 'addNew'
        }
        if (action === 'activity') {
          action = {type: ''}
          action.type = 'activity'
        }
        await actionMapper[action.type]?.(action.data);
        data.isLoading = true;
      } catch (e) {
        console.error("Error: ", e);
      } finally {
        data.isLoading = false;
      }
    };

    const validateTag = (tagName) => {
      tagName = tagName.trim();
      const regex = "^[^<>|%]+$";
      let errMsg = "";
      if (isEmpty(tagName)) {
        errMsg = "Tag Name cannot be empty";
      } else if (!inRange(tagName.length, 3, 33)) {
        errMsg = "Tag name should be betweeen 3 to 32 character in length!";
      } else if (!RegExp(regex).test(tagName)) {
        errMsg = "Tag names cannot contain %, |, < and >";
      } else if (data.existingNames.includes(tagName.toLowerCase())) {
        errMsg = `The Tag '${tagName}' is Already Present, Tag names have to be unique!`;
      }
      return errMsg;
    };

    const addTag = async (tags) => {
      data.isLoading = true;
      try {
        const result = await Tags.addTags(tags);
        const { valids, duplicates, limitReached } = result;
        data.isLoading = false;
        if (limitReached) {
          toast.error(
            `Can not add more tags, Company tag limit of 1024 reached!`
          );
        }
        if (!isEmpty(valids)) {
          const importedTagNames = map(valids, "name");
          toast.success(`${importedTagNames} Tag Saved Successfully!`);
        }
        if (!isEmpty(duplicates)) {
          toast.error(
            `The name provided for "${duplicates}" is already present!`
          );
        }
      } catch (e) {
        data.isLoading = false;
        console.error("Error: ", e);
      } finally {
        data.isLoading = false;
      }
    };

    const refreshTable = () => {
      data.newName = "";
      data.showAddEditModal = false;
      data.selectedRow = {};
      data.rerenderKey++;
      data.editMode = false;
    };

    const updateTag = async (tag) => {
      data.isLoading = true;
      try {
        const updatedTag = await Tags.updateTag(tag);
        data.existingNames = [];
        if (!isEmpty(updatedTag.data.duplicates)) {
          toast.error(
            `The name provided for "${tag.name}" is already present!`
          );
          return;
        }
        toast.success(`${tag.name} Updated Successfully!`, "is-success");
        refreshTable();
      } catch (e) {
        console.error("Error: ", e);
      } finally {
        data.isLoading = false;
      }
    };

    const addUpdateTag = async () => {
      const validationResult = validateTag(data.newName);
      if (validationResult) {
        toast.error(validationResult);
        return false;
      }
      if (!data.editMode) {
        const obj = {
          name: data.newName,
          color: "#43AAC7",
          scope: "project",
          project: props.rowData._id,
        };
        await addTag(obj);
        refreshTable();
      }
      if (data.editMode) {
        const obj = data.selectedRow;
        obj.name = data.newName;
        updateTag(obj);
      }
    }

    const checkDuplicate = () => {
      const vals = filter(data.tableData, (row) => row.uuid !== data.selectedRow.uuid)
      if (!isEmpty(vals)) {
        return vals.map((row) => row.name).includes(data.newName)
      }
      return false;
    }

    const addUpdateLevel = async () => {
      try {
        data.isLoading = true
        if (!data.editMode) {
          if (data.project.levels.includes(data.newName)) {
            toast.error(`${data.newName} already exist`)
            return false;
          }
          data.project.levels.push(data.newName)
          data.project = await Projects.update(data.project);
          refreshTable();
        } else {
          if (!checkDuplicate()) {
            await Projects.reNameLAZ({
              oldName: data.selectedRow.name,
              type: 'level',
              newName: data.newName,
              projectId: data.project._id,
            });
            toast.success(`${data.newName} Renamed Successfully`)
            data.project = await Projects.getOne(props.rowData._id);
            refreshTable()
          } else  {
            toast.error(`${data.newName} already exist`)
            return false
          }
        }
      } catch (e) {
        console.error('Error: ', e)
      } finally {
        data.isLoading = false
      }
    }

    const addUpdateZone = async () => {
      try {
        data.isLoading = true
        if (!data.editMode) {
          if (data.project.zones.includes(data.newName)) {
            toast.error(`${data.newName} already exist`)
            return;
          }
          data.project.zones.push(data.newName)
          data.project = await Projects.update(data.project);
          await refreshTable();
        } else {
          if (!checkDuplicate()) {
            await Projects.reNameLAZ({
              oldName: data.selectedRow.name,
              type: 'zone',
              newName: data.newName,
              projectId: data.project._id,
            });
            toast.success(`${data.newName} Renamed Successfully`)
            data.project = await Projects.getOne(props.rowData._id);
            refreshTable()
          } else  {
            toast.error(`${data.newName} already exist`)
            return false
          }
        }
      } catch (e) {
        console.error('Error: ', e)
      } finally {
        data.isLoading = false
      }
    }

    const handleAddUpdate = async () => {
      if (selectedTab.value.id === 'tag') await addUpdateTag()
      if(selectedTab.value.id === 'level') await addUpdateLevel()
      if(selectedTab.value.id === 'zone') await addUpdateZone()
    };

    onBeforeMount(async () => {
      data.project = await Projects.getOne(props.rowData._id);
    })

    const getModalTitle = computed(() => {
      if (selectedTab.value.id === 'level') return 'Level'
      if (selectedTab.value.id === 'zone') return 'Zone'
      return 'Tag'
    })

    const getModalInputLabel = computed(() => {
      if (selectedTab.value.id === 'level') return 'Level Name'
      if (selectedTab.value.id === 'zone') return 'Zone Name'
      return 'Tag Name'
    })

    const showImportLvlZone = computed(() => {
      if (!isEmpty(data.project) && ((selectedTab.value.id === 'level' && !data.project?.levels?.length) ||
        (selectedTab.value.id === 'zone' && !data.project?.zones?.length))) {
        return true
      }
      return false;
    })

    const importLAZ = async () => {
      const fieldType = selectedTab.value.id === 'level' ? 'levels' : 'zones'
      const levelsAndZons = await Projects.getLAZFromCards({
        projectId: props.rowData._id,
        fieldType,
      });
      if (levelsAndZons.data.length > 0 && ['levels', 'zones'].includes(fieldType)) {
        DialogProgrammatic.confirm({
          title: `use pre-defined ${fieldType}`,
          message: `If you continue,all ${fieldType} will be defined here,in project settings`,
          okButton: "Yes",
          cancelButton: "Cancel",
          onConfirm: async () => {
            try {
              data.isLoading = true;
              data.project[fieldType] = levelsAndZons.data;
              data.project = await Projects.update(data.project);
              refreshTable();
              toast.success(`Imported pre-defined ${fieldType}`);
            } catch (error) {
              console.error("Error", error);
            } finally {
              data.isLoading = false;
            }
          },
        });
      } else {
        toast.warning(`No ${fieldType} found`)
      }
    }

    const isDisabledOnPermission = computed(() => {
      return getPermissionValues({
        permissionModule: 'app&projects',
        type: 'alterProject',
      })
    })

    return {
      ...toRefs(data),
      tabs,
      loadData,
      tableColOpts,
      handleAction,
      handleAddUpdate,
      setData,
      getModalTitle,
      getModalInputLabel,
      showImportLvlZone,
      importLAZ,
      isDisabledOnPermission
    };
  },
};
</script>
